import React, { useState, useEffect } from "react";
import {
  Box, Button, Typography, Dialog, DialogActions, DialogContent, DialogTitle,
  useMediaQuery, useTheme, Alert
} from "@mui/material";
import { useCoupons } from "../../hooks/useCoupons";
import CouponTable from "../../components/CouponTable";
import AddCouponForm from "../../components/AddCouponForm";
import { usePageContext } from "../../context/PageContext";
import { useTranslation } from "react-i18next";

const Coupons = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, error, refetch, updateMutation, deleteMutation, isLoading } =
    useCoupons(currentPage, pageSize, search);

  useEffect(() => {
    if (error) setErrorMessage("Erro ao carregar cupons. Por favor, tente novamente.");
    else setErrorMessage(null);
  }, [error]);

  const handleEditCoupon = (coupon) => {
    setSelectedCoupon(coupon);
    setErrorMessage(null);
  };

  const handleDeleteCoupon = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteCoupon = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setErrorMessage(err?.response?.data?.message || "Erro ao excluir o cupom.");
      },
      onSettled: () => setIsProcessing(false),
    });
  };

  const handleToggleStatus = (row) => {
    const nextStatus = Number(row.status) === 1 ? 0 : 1;
    setIsProcessing(true);
    updateMutation.mutate(
      { id: row.id, status: nextStatus },
      {
        onSuccess: () => refetch(),
        onError: (err) => {
          setErrorMessage(err?.response?.data?.message || "Erro ao atualizar o status do cupom.");
        },
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);
  const handleSearch = (term) => {
    setSearch(term);
    refetch();
  };
  const handleCloseForm = () => {
    setSelectedCoupon(null);
    setErrorMessage(null);
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    setIsProcessing(true);
    setErrorMessage(null);
    const results = await Promise.allSettled(
      ids.map((id) => deleteMutation.mutateAsync(id))
    );
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      setErrorMessage(
        failed.length === ids.length
          ? "Erro ao excluir os cupons."
          : `${failed.length} de ${ids.length} exclusão(ões) falharam.`
      );
    }
    refetch();
    setIsProcessing(false);
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>{t("couponsPage.heading")}</Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => setSelectedCoupon({})}
        >
          {t("couponsPage.addCoupon")}
        </Button>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedCoupon ? (
        <AddCouponForm
          couponData={selectedCoupon}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSubmit={(updatedCoupon) => {
            setIsProcessing(true);
            updateMutation.mutate(updatedCoupon, {
              onSuccess: () => {
                refetch();
                handleCloseForm();
              },
              onError: (err) =>
                setErrorMessage(err?.response?.data?.message || "Erro ao salvar o cupom."),
              onSettled: () => setIsProcessing(false),
            });
          }}
        />
      ) : (
        <CouponTable
          coupons={data?.coupons || []}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onEditCoupon={handleEditCoupon}
          onDeleteCoupon={handleDeleteCoupon}
          onDeleteSelected={handleDeleteSelected}
          onToggleStatus={handleToggleStatus}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t("shared.deleteConfirmation")}</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? t("shared.processingDelete") : t("couponsPage.deleteQuestion")}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>{t("shared.cancel")}</Button>
              <Button color="error" onClick={confirmDeleteCoupon}>{t("shared.delete")}</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Coupons;
